import { useMemo } from 'react';
import type { Quote } from '@/model/quotesModel';
import { useAdjustedQuotes } from '@/features/shop-insurance/hooks/use-adjusted-quotes';
import { useBenefitDesignQuotes } from '@/features/shop-insurance/hooks/use-benefit-design-quotes';
import { useAllPaymentOptionQuotesFetching } from '@/features/shop-insurance/hooks/use-all-payment-option-quotes-fetching';
import {
  useSelectedFeatureBenefitsFilters,
  useIncludeCouplesDiscount,
} from '@/features/shop-insurance/stores/eligibility-filters-store';
import { useHiddenCarriers } from '@/features/shop-insurance/stores/carrier-exclusions-store';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useSelectedBenefitPaymentType } from '@/features/shop-insurance/stores/benefit-type-filter-store';
import type {
  BenefitDesignInputs,
  CombinedFilterType,
  InflationTypeFilterValue,
  ShopInsuranceMode,
  TraditionalLTCQuoteWithROI,
} from '@/features/shop-insurance/types';

interface UseShopInsuranceModeQuotesParams {
  mode: ShopInsuranceMode;
  selectedTab: ShopInsuranceTab;
  client: Client | undefined;
  quotes: Quote[] | undefined;
  allPaymentOptionQuotes: Quote[];
  premiumValue: number;
  benefitDurationValue: CombinedFilterType;
  /** Minimum inflation protection %; 0 means unfiltered. Used when the flag is on. */
  inflationProtectionValue: number;
  /** Legacy AOB/COB range; used when `inflationProtectionMinimum` is off. */
  inflationProtectionRangeValue: CombinedFilterType;
  inflationTypeFilterValue: InflationTypeFilterValue;
  benefitDesignInputs: BenefitDesignInputs;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
  hasPartnerWithDemographics: boolean;
  filteredTraditionalLTCQuotes: TraditionalLTCQuoteWithROI[];
  isGapFillMode?: boolean;
  totalCost?: number;
}

export function useShopInsuranceModeQuotes({
  mode,
  selectedTab,
  client,
  quotes,
  allPaymentOptionQuotes,
  premiumValue,
  benefitDurationValue,
  inflationProtectionValue,
  inflationProtectionRangeValue,
  inflationTypeFilterValue,
  benefitDesignInputs,
  returnOnInvestmentCalculationType,
  hasPartnerWithDemographics,
  filteredTraditionalLTCQuotes,
  isGapFillMode,
  totalCost,
}: UseShopInsuranceModeQuotesParams) {
  const selectedFeatureBenefitsFilters = useSelectedFeatureBenefitsFilters();
  const includeCouplesDiscount = useIncludeCouplesDiscount();
  const selectedBenefitPaymentType = useSelectedBenefitPaymentType();
  const hiddenCarriers = useHiddenCarriers();
  const { flags } = useFeatureFlags();
  const useMinimumInflation = flags.inflationProtectionMinimum ?? false;

  useAllPaymentOptionQuotesFetching({
    mode,
    selectedTab,
    hasPartnerWithDemographics,
  });

  const { adjustedQuotes, isAdjusted } = useAdjustedQuotes({
    client,
    selectedTab,
    returnOnInvestmentCalculationType,
    originalQuotes: quotes,
    selectedPremium: premiumValue,
    selectedBenefitDuration: benefitDurationValue,
    selectedInflationProtection: inflationProtectionValue,
    selectedInflationProtectionRange: inflationProtectionRangeValue,
    useMinimumInflation,
    selectedInflationTypeFilter: inflationTypeFilterValue,
    selectedFeatureBenefitsFilters,
    selectedBenefitPaymentType,
    includeCouplesDiscount,
    isGapFillMode,
    totalCost,
  });

  const { quoteGroups: benefitDesignQuoteGroups, availableOptions: benefitDesignAvailableOptions } =
    useBenefitDesignQuotes({
      originalQuotes: allPaymentOptionQuotes.length ? allPaymentOptionQuotes : quotes,
      client,
      selectedTab,
      inputs: benefitDesignInputs,
      returnOnInvestmentCalculationType,
      useMinimumInflation,
    });

  const filteredBenefitDesignQuoteGroups = useMemo(
    () =>
      hiddenCarriers.length
        ? benefitDesignQuoteGroups.filter(g => !hiddenCarriers.includes(g.carrierName))
        : benefitDesignQuoteGroups,
    [benefitDesignQuoteGroups, hiddenCarriers],
  );

  const benefitDesignFlatQuotes = useMemo(
    () => filteredBenefitDesignQuoteGroups.map(group => group.representativeQuote),
    [filteredBenefitDesignQuoteGroups],
  );

  const modeAdjustedQuotes = useMemo(() => {
    const base = mode === 'benefit-design' ? benefitDesignFlatQuotes : adjustedQuotes;
    if (!hiddenCarriers.length) return base;
    return base.filter(q => !hiddenCarriers.includes(q.carrierName));
  }, [mode, benefitDesignFlatQuotes, adjustedQuotes, hiddenCarriers]);

  const modeTraditionalLTCQuotes = useMemo(() => {
    if (mode === 'benefit-design') return [];
    if (!hiddenCarriers.length) return filteredTraditionalLTCQuotes;
    return filteredTraditionalLTCQuotes.filter(q => !hiddenCarriers.includes(q.carrier));
  }, [mode, filteredTraditionalLTCQuotes, hiddenCarriers]);

  return {
    adjustedQuotes,
    isAdjusted,
    benefitDesignQuoteGroups: filteredBenefitDesignQuoteGroups,
    benefitDesignAvailableOptions,
    modeAdjustedQuotes,
    modeTraditionalLTCQuotes,
    selectedFeatureBenefitsFilters,
    hiddenCarriers,
  };
}
